import { KeyboardEvent, PointerEvent, ReactNode, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

const MIN_WIDTH = 200
/** The chat column (the panel's previous sibling) never gets narrower than this. */
const MIN_CHAT_WIDTH = 360
const KEY_STEP = 32

/** Panel + chat column: all the room the panel can take, whatever its width right now (even mid-animation). */
const getMaxWidth = (aside: HTMLElement | null) => {
  const room =
    (aside?.getBoundingClientRect().width ?? 0) + (aside?.previousElementSibling?.getBoundingClientRect().width ?? 0)
  return Math.max(MIN_WIDTH, room - MIN_CHAT_WIDTH)
}

type ResizablePanelProps = {
  /** Width to glide to whenever it changes (prompts ↔ PDF); a manual resize holds until the next change. */
  defaultWidth: number
  children: ReactNode
}

/**
 * Right column of the notebook, resizable like Claude's artifact panel: drag its left edge (or
 * focus it and use the arrow keys). The chat column absorbs the difference.
 */
export const ResizablePanel = ({ defaultWidth, children }: ResizablePanelProps) => {
  const { t } = useTranslation()
  const [width, setWidth] = useState(defaultWidth)
  const [dragging, setDragging] = useState(false)
  const asideRef = useRef<HTMLElement>(null)
  const dragRef = useRef({ startX: 0, startWidth: 0, maxWidth: 0 })

  // New default (mode change): animate there, clamped so the chat keeps its minimum on small screens.
  // ponytail: not re-clamped on window resize; add a ResizeObserver if a shrunk window squeezes the chat.
  useLayoutEffect(() => {
    setWidth(Math.min(defaultWidth, getMaxWidth(asideRef.current)))
  }, [defaultWidth])

  const clamp = (value: number, maxWidth: number) => Math.min(Math.max(value, MIN_WIDTH), maxWidth)

  // The whole page keeps the resize cursor and selects no text while the pointer crosses the chat.
  const setDragStyles = (on: boolean) => {
    document.body.style.cursor = on ? 'col-resize' : ''
    document.body.style.userSelect = on ? 'none' : ''
    setDragging(on)
  }

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId)
    // rendered width, not state: a drag can start while the panel is still gliding
    const startWidth = asideRef.current?.getBoundingClientRect().width ?? width
    dragRef.current = { startX: event.clientX, startWidth, maxWidth: getMaxWidth(asideRef.current) }
    setDragStyles(true)
  }

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
    const { startX, startWidth, maxWidth } = dragRef.current
    // The handle is on the panel's LEFT edge: moving left widens it.
    setWidth(clamp(startWidth + startX - event.clientX, maxWidth))
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta = event.key === 'ArrowLeft' ? KEY_STEP : event.key === 'ArrowRight' ? -KEY_STEP : 0
    if (!delta) return
    event.preventDefault()
    const maxWidth = getMaxWidth(asideRef.current)
    setWidth((current) => clamp(current + delta, maxWidth))
  }

  return (
    <aside
      ref={asideRef}
      style={{ width }}
      className={clsx(
        'relative flex shrink-0 flex-col border-l border-border bg-background',
        // the PDF preview iframe would swallow the pointer mid-drag; a drag follows the pointer, no easing
        dragging
          ? '[&_iframe]:pointer-events-none'
          : 'transition-[width] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none',
      )}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t('notebookResizePanel')}
        title={t('notebookResizePanel')}
        aria-valuenow={width}
        aria-valuemin={MIN_WIDTH}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={() => setDragStyles(false)}
        onPointerCancel={() => setDragStyles(false)}
        onKeyDown={onKeyDown}
        className="group absolute inset-y-0 -left-4 z-10 flex w-6 cursor-col-resize touch-none justify-center outline-none"
      >
        {/* Grip pill on the divider: light primary on hover/focus says "draggable", full primary while dragging. */}
        <span
          className={clsx(
            // just left of the divider line, like Claude's grip
            'h-28 w-1.5 -translate-x-1.25 self-center rounded-full transition-colors duration-150',
            dragging
              ? 'bg-primary'
              : 'bg-transparent group-hover:bg-primary/40 group-focus-visible:bg-primary/40 dark:group-hover:bg-primary/70 dark:group-focus-visible:bg-primary/70',
          )}
        />
      </div>
      {children}
    </aside>
  )
}
